import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Save, X, Loader2, Calendar, Upload, FileText, Download } from 'lucide-react';
import { saveTimetable, getTimetables, deleteTimetable } from '../../firebase/collections';
import { uploadFile } from '../../firebase/r2storage';
import { getBatches } from '../../firebase/collections';
import { useRealtime } from '../../contexts/RealtimeContext';
import toast from 'react-hot-toast';

const TimetableManagement = () => {
  const { userProfile } = useRealtime();
  const [timetables, setTimetables] = useState([]);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    pdfUrl: '',
    batchId: '',
    isGlobal: true
  });

  const isSuperAdmin = userProfile?.role === 'admin' || userProfile?.role === 'superadmin' || userProfile?.role === 'founder';
  const coachingId = isSuperAdmin ? null : (userProfile?.coachingId || null);

  useEffect(() => {
    loadTimetables();
    loadBatches();
  }, []);

  const loadTimetables = async () => {
    try {
      setLoading(true);
      const filters = coachingId ? { coachingId } : { isGlobal: true };
      const result = await getTimetables(filters);
      if (result.success) {
        setTimetables(result.data || []);
      } else {
        toast.error('Failed to load timetables');
      }
    } catch (error) {
      toast.error('Error loading timetables');
    } finally {
      setLoading(false);
    }
  };

  const loadBatches = async () => {
    try {
      const result = await getBatches(coachingId ? { coachingId } : {});
      if (result.success) {
        setBatches(result.data || []);
      }
    } catch (error) {
      console.error('Error loading batches:', error);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFileUpload = async (file) => {
    if (!file) return;
    
    if (file.type !== 'application/pdf') {
      toast.error('Please upload a PDF file');
      return;
    }

    try {
      setUploading(true);
      const fileName = `timetables/${Date.now()}_${file.name}`;
      const result = await uploadFile(file, fileName);
      
      if (result.success) {
        setFormData(prev => ({ ...prev, pdfUrl: result.url }));
        toast.success('PDF uploaded successfully');
      } else {
        toast.error('Failed to upload PDF: ' + result.error);
      }
    } catch (error) {
      toast.error('Error uploading file: ' + error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleEdit = (timetable) => {
    setFormData({
      title: timetable.title || '',
      description: timetable.description || '',
      pdfUrl: timetable.pdfUrl || '',
      batchId: timetable.batchId || '',
      isGlobal: timetable.isGlobal !== undefined ? timetable.isGlobal : true
    });
    setEditingId(timetable.id);
    setShowForm(true);
  };

  const handleCancel = () => {
    setFormData({
      title: '',
      description: '',
      pdfUrl: '',
      batchId: '',
      isGlobal: true
    });
    setEditingId(null);
    setShowForm(false);
  };

  const handleSave = async () => {
    if (!formData.title.trim()) {
      toast.error('Please enter timetable title');
      return;
    }
    if (!formData.pdfUrl) {
      toast.error('Please upload a PDF file');
      return;
    }

    try {
      setSaving(true);
      const result = editingId
        ? await saveTimetable({ ...formData, id: editingId, coachingId })
        : await saveTimetable({ ...formData, coachingId });

      if (result.success) {
        toast.success(editingId ? 'Timetable updated successfully' : 'Timetable added successfully');
        handleCancel();
        loadTimetables();
      } else {
        toast.error('Failed to save: ' + result.error);
      }
    } catch (error) {
      toast.error('Error saving timetable: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this timetable?')) {
      return;
    }

    try {
      const result = await deleteTimetable(id);
      if (result.success) {
        toast.success('Timetable deleted successfully');
        loadTimetables();
      } else {
        toast.error('Failed to delete: ' + result.error);
      }
    } catch (error) {
      toast.error('Error deleting timetable: ' + error.message);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Timetable Management</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Manage timetables for batches or create global timetables
          </p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
        >
          <Plus className="w-5 h-5" />
          Add Timetable
        </button>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              {editingId ? 'Edit Timetable' : 'Add New Timetable'}
            </h2>
            <button
              onClick={handleCancel}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Title *
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                placeholder="e.g., Weekly Timetable"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                rows={3}
                placeholder="Optional description"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Scope
              </label>
              <select
                value={formData.isGlobal ? 'global' : 'batch'}
                onChange={(e) => handleInputChange('isGlobal', e.target.value === 'global')}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="global">Global (All Students)</option>
                <option value="batch">Specific Batch</option>
              </select>
            </div>

            {!formData.isGlobal && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Select Batch
                </label>
                <select
                  value={formData.batchId}
                  onChange={(e) => handleInputChange('batchId', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">Select a batch</option>
                  {batches.map(batch => (
                    <option key={batch.id} value={batch.id}>{batch.name}</option>
                  ))}
                </select>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                PDF File *
              </label>
              {formData.pdfUrl ? (
                <div className="flex items-center gap-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <FileText className="w-8 h-8 text-blue-600" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">PDF Uploaded</p>
                    <a
                      href={formData.pdfUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:underline"
                    >
                      View PDF
                    </a>
                  </div>
                  <button
                    onClick={() => handleInputChange('pdfUrl', '')}
                    className="text-red-600 hover:text-red-800"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              ) : (
                <label className="flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700">
                  {uploading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>Uploading...</span>
                    </>
                  ) : (
                    <>
                      <Upload className="w-5 h-5" />
                      <span>Upload PDF (Max 10MB)</span>
                    </>
                  )}
                  <input
                    type="file"
                    accept="application/pdf"
                    className="hidden"
                    onChange={(e) => {
                      if (e.target.files[0]) {
                        handleFileUpload(e.target.files[0]);
                      }
                    }}
                    disabled={uploading}
                  />
                </label>
              )}
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <button
                onClick={handleCancel}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving || uploading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 disabled:opacity-50"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Save
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Timetables List */}
      {timetables.length === 0 && !showForm ? (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-12 text-center">
          <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">No timetables yet. Add your first timetable!</p>
        </div>
      ) : (
        !showForm && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {timetables.map((timetable) => (
              <div key={timetable.id} className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                      {timetable.title}
                    </h3>
                    {timetable.description && (
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                        {timetable.description}
                      </p>
                    )}
                    <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                      <span className={`px-2 py-1 rounded ${timetable.isGlobal ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300' : 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300'}`}>
                        {timetable.isGlobal ? 'Global' : 'Batch'}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleEdit(timetable)}
                      className="text-blue-600 hover:text-blue-800"
                      title="Edit"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(timetable.id)}
                      className="text-red-600 hover:text-red-800"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                {timetable.pdfUrl && (
                  <a
                    href={timetable.pdfUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    <Download className="w-4 h-4" />
                    View/Download PDF
                  </a>
                )}
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
};

export default TimetableManagement;

